import React, { useRef, useState } from "react";
import { collection, documentId, limit, orderBy, query, startAfter } from "firebase/firestore";
import { buildQuoteActivityMigrationPreview } from "./quoteActivityMigrationUtils";

function dateLabel(value) {
  try {
    if (value == null) return "None";
    if (value?._methodName) return "Legacy sentinel map";
    const date = value?.toDate ? value.toDate() : new Date(value);
    return Number.isFinite(date.getTime()) ? date.toISOString() : "Invalid/ambiguous";
  } catch { return "Invalid/ambiguous"; }
}

// Intentionally has no write capability. Migration approval is a separate checkpoint.
export default function CrmActivityMigrationPreview({ db, workspaceUid, allowed, getDocs, styles = {} }) {
  const [sourceCollection, setSourceCollection] = useState("quote_index");
  const [page, setPage] = useState({ rows: [], cursor: null, hasMore: false, loaded: false, loading: false, error: "" });
  const busy = useRef(false);
  const scope = `${workspaceUid}:${sourceCollection}`;
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const visiblePage = page.scope === scope ? page : { rows: [], hasMore: false, loading: false, loaded: false };
  if (!allowed) return null;
  const load = async (append) => {
    if (!workspaceUid || busy.current) return;
    const requestScope = scope;
    busy.current = true;
    setPage((prev) => ({ ...(prev.scope === scope ? prev : { rows: [] }), scope, loading: true, error: "" }));
    try {
      const constraints = [orderBy(documentId()), limit(25)];
      if (append && page.scope === scope && page.cursor) constraints.push(startAfter(page.cursor));
      const snap = await getDocs(query(collection(db, "users", workspaceUid, sourceCollection), ...constraints), {
        label: "crmDataRepair.activityMigrationPreview", ttlMs: 0,
      });
      if (scopeRef.current !== requestScope) return;
      const rows = snap.docs.map((item) => buildQuoteActivityMigrationPreview(item.id, item.data()));
      setPage((prev) => ({ scope, rows: append && prev.scope === scope ? [...prev.rows, ...rows] : rows,
        cursor: snap.docs[snap.docs.length - 1] || null, hasMore: snap.size === 25,
        loaded: true, loading: false, error: "" }));
    } catch {
      if (scopeRef.current !== requestScope) return;
      setPage((prev) => ({ ...prev, loading: false, error: "Could not load preview. Check your repair access and retry." }));
    } finally { busy.current = false; }
  };
  return <details style={styles.card}>
    <summary>Historical sorting date review (read-only)</summary>
    <p>No dates are changed here. A dedicated sorting date is only a proposal; it is not active. Creation/issue dates are not verified business activity. Production migration requires separate approval.</p>
    <label>Review records <select value={sourceCollection} disabled={visiblePage.loading} onChange={(event) => setSourceCollection(event.target.value)}>
      <option value="quote_index">Quotation indexes</option><option value="crm_leads">CRM deals</option>
    </select></label>
    <button type="button" style={styles.button} disabled={visiblePage.loading} onClick={() => load(false)}>Preview first 25 records</button>
    {visiblePage.hasMore && <button type="button" style={styles.button} disabled={visiblePage.loading} onClick={() => load(true)}>Preview next 25 records</button>}
    {visiblePage.error && <p role="alert">{visiblePage.error}</p>}
    {visiblePage.loading && <p role="status">Loading preview...</p>}
    {visiblePage.rows.map((row) => <div key={row.id} style={{ padding: 10, borderBottom: "1px solid #94a3b8" }}>
      <strong>{row.quoteNo || row.id}</strong> <small>{row.id}</small>
      <div>Current: {dateLabel(row.current)} | Classification: {row.classification}</div>
      <div>Proposed sorting fallback: {dateLabel(row.proposed)} | Source: {row.source} | Review class: {row.reviewClass}</div>
      <div>A: existing activity, B: limited fallback, C: manual review, D: date unavailable. No repair action is available here.</div>
      <div>Available history: {row.candidates.length ? row.candidates.map((candidate) => `${candidate.source}: ${dateLabel(candidate.value)}`).join(" | ") : "None"}</div>
      <div>{row.reason}</div>
    </div>)}
    {visiblePage.loaded && !visiblePage.hasMore && <p>End of preview pages. No records were changed.</p>}
  </details>;
}
